import { isAfter, isBefore, subHours } from 'date-fns';
import {
  type EventContentObjectDetail,
  type EventPrivacy,
  type Program,
  type ProgramEvent,
  type ProgramEventType,
  ProgramInviteType,
  generateProgramEventStartDate,
  getProgramEventEndDate,
  hasProgramEventFinished,
  isProgramEventOngoing,
} from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AttachmentsInput,
  type AttahcmentsInputUploadFile,
  CardTabDeprecated,
  CardTabsDeprecated,
  type DatePickerDateType,
  type FileUploadDone,
  Form,
  FormActions,
  type FormInstance,
  ImageDraggerWithCrop,
  Input,
  Modal,
  RichTextInput,
  SelectInput,
  Title,
  type UploadChangeParam,
  type UploadedFileList,
  getDatePickerDateType,
  getDateType,
  useGenerateImageUploadRulesAndValidations,
} from 'ui';
import { getBrowserTimezoneWithOffset, loadImage } from 'common/utils';
import { containsEmoji } from 'common/validators';
import { uploadImage } from 'features/files';
import type { EmailSelectedUserWithRole, RegisteredSelectedUserWithRole } from '../../../../types';
import { EventInviteesSelects } from '../../../event-invitees-selects';
import { DuplicateInviteesSection } from '../duplicate-invitees-section';
import {
  type CommonFormValues,
  EventInviteesPicker,
  type RequiredFormValues,
  useEventInvitees,
} from '../event-invitees-picker';
import EventCommunity, { type CommunityOption } from '../EventCommunity';
import EventPublish from '../EventPublish';
import EventTime from '../EventTime';
import { useGetButtonTitles } from '../hooks';
import EventChannelsOptions from './EventChannelsOptions';
import EventInviteesExceededLimit, { type ExceedCommonFormValues } from './EventInviteesExceededLimit';
import { useDefaultEventFormInitialValues } from './hooks';

export type DefaultEventData = {
  eventType: ProgramEventType;
  formValues: DefaultEventFormValues;
};

export type DefaultEventFormValues = {
  community?: CommunityOption;
  coverPhoto?: [FileUploadDone];
  duplicateInviteesFromEvent?: string;
  duration: number;
  emailInvitees?: EmailSelectedUserWithRole[];
  endTime: DatePickerDateType;
  eventsFiles?: EventFiles;
  featuredInChannels?: boolean;
  htmlContent?: string;
  id?: string;
  invitees?: RegisteredSelectedUserWithRole[];
  name: string;
  organizationId?: string;
  programId?: string;
  programTopicId?: string;
  publishAt?: DatePickerDateType;
  showInChannels?: boolean;
  startTime: DatePickerDateType;
  tags?: string[];
  textContent?: string;
  viewPermission: EventPrivacy;
};

// TODO this still is not correct but it is close
type EventFiles =
  | { fileList: UploadChangeParam<AttahcmentsInputUploadFile<{ id: string }>>['fileList'] }
  | UploadChangeParam<AttahcmentsInputUploadFile<{ id: string }>>;

type P = {
  eventContentObject?: EventContentObjectDetail;
  eventType: ProgramEventType;
  initialData?: Partial<DefaultEventFormValues>;
  inviteType: ProgramInviteType;
  isLoading: boolean;
  mode: 'create' | 'duplicate' | 'edit';
  onCancel?: () => void;
  onInviteesTypeChanged: (type: ProgramInviteType) => void;
  onSubmit: (formData: DefaultEventData, event?: ProgramEvent) => Promise<void> | void;
  program?: Program;
};

const DefaultEventForm = ({
  eventContentObject,
  eventType,
  initialData,
  inviteType,
  isLoading,
  mode,
  onCancel,
  onInviteesTypeChanged,
  onSubmit,
  program,
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity -- TODO: fix
}: P) => {
  const isProgram = Boolean(program);
  const event = eventContentObject?.event;
  const isDuplicating = mode === 'duplicate';

  const { t } = useTranslation();
  const { offset, timeZone } = getBrowserTimezoneWithOffset();
  const [form] = Form.useForm<DefaultEventFormValues>();
  const { cancelTitle, submitTitle } = useGetButtonTitles({ mode });

  const initialValues = useDefaultEventFormInitialValues({
    eventContentObject,
    initialData,
    isDuplicating,
    isProgram,
    program,
  });

  const { rules: imageRules, validations: imageValidations } = useGenerateImageUploadRulesAndValidations();

  const [minimumStart, setMinimumStart] = useState(
    generateProgramEventStartDate({ publishAt: isProgram ? getDateType(initialValues.publishAt) : undefined }),
  );

  const { onInvite, onShowInvitees, resetSelectedUsers } = useEventInvitees<DefaultEventFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Antd interface need to be casted to the correct type
    form: form as unknown as FormInstance<RequiredFormValues>,
    onInviteesTypeChanged,
    skipPostInCommunityValidation: isProgram,
  });

  const onUpdatePublish = (publishAt: DatePickerDateType | null) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    const start: DatePickerDateType = form.getFieldValue('startTime');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    const duration: number = form.getFieldValue('duration');
    const minimum = generateProgramEventStartDate({ publishAt: getDateType(publishAt) ?? undefined });

    setMinimumStart(minimum);
    if (isBefore(getDateType(start), minimum)) {
      form.setFieldsValue({
        endTime: getDatePickerDateType(getProgramEventEndDate({ duration, startTime: minimum })),
        startTime: getDatePickerDateType(minimum),
      });
    }
  };

  const resetChannelsOptions = () => {
    form.setFieldsValue({ featuredInChannels: false, showInChannels: false, tags: [] });
  };

  const onFinish = async (formValues: DefaultEventFormValues) => onSubmit({ eventType, formValues }, event);

  return (
    <Form<DefaultEventFormValues> form={form} initialValues={initialValues} name="event-form" onFinish={onFinish}>
      {inviteType ? (
        <Form.Item<DefaultEventFormValues> dependencies={['community']} noStyle>
          {() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            const formValues: Partial<DefaultEventFormValues> = form.getFieldsValue(true);

            return (
              <EventInviteesPicker
                communityId={formValues.community?.value}
                // TODO: This is hack
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                form={form as unknown as FormInstance<CommonFormValues>}
                inviteType={inviteType}
                onBack={onCancel}
                onInvite={onInvite}
                programId={program?.id}
              />
            );
          }}
        </Form.Item>
      ) : (
        <>
          <Title level={4} marginBottom>
            Event details
          </Title>
          {program ? (
            <>
              <Form.Item<DefaultEventFormValues> hidden name="programId">
                <Input />
              </Form.Item>
              <SelectInput<{
                description: string;
                id: string;
                name: string;
              }>
                input={{
                  getOptionLabel: (option) => option.name,
                  getOptionValue: (option) => option.id,
                  options: program.topics,
                  placeholder: t('Please select the module the event belongs to'),
                }}
                item={{
                  'data-test-id': 'select_program-topic',
                  label: 'Program module',
                  name: 'programTopicId',
                  rules: [
                    {
                      required: true,
                    },
                  ],
                }}
              />
              <EventPublish
                input={{
                  maximum: subHours(program.endTime, 24),
                  minimum: program.startTime,
                  onChange: onUpdatePublish,
                }}
                item={{
                  label: 'Publish date',
                  name: 'publishAt',
                  rules: [
                    {
                      required: true,
                    },
                    {
                      // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                      async validator(_: unknown, value?: DatePickerDateType) {
                        if (value) {
                          if (isBefore(getDateType(value), program.startTime)) {
                            throw new Error(t('Error: Post publish date must be set after Program start time'));
                          }

                          if (isAfter(getDateType(value), program.endTime)) {
                            throw new Error(t('Error: Post publish date must be set before Program end time'));
                          }
                        }
                      },
                    },
                  ],
                }}
              />
            </>
          ) : (
            <EventCommunity
              input={{
                onChange(selectedCommunity?: CommunityOption | CommunityOption[]) {
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  resetSelectedUsers?.();
                  // TODO find better solution how to reset tags on change of Organization
                  const newOrganizationId =
                    selectedCommunity && Array.isArray(selectedCommunity)
                      ? selectedCommunity[0]?.data.organization.id
                      : selectedCommunity?.data.organization.id;
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  const { organizationId: currentOrganizationId } = form.getFieldsValue(['organizationId']);

                  if (currentOrganizationId !== newOrganizationId) {
                    form.setFieldsValue({ organizationId: newOrganizationId });
                    resetChannelsOptions();
                  }
                },
                query: { filter: { scope: eventType === 'conference' ? 'create_conference' : 'create_webinar' } },
              }}
              item={{
                label: 'Select community to invite',
                rules: [{ required: true }],
              }}
            />
          )}
          <Form.Item<DefaultEventFormValues>
            label={eventType === 'webinar' ? 'Webinar / Webcast name' : 'Video conference name'}
            name="name"
            rules={[
              {
                required: true,
                whitespace: true,
              },
              {
                // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                async validator(_, value) {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  if (containsEmoji(value)) {
                    // eslint-disable-next-line no-template-curly-in-string -- Need to have ${label} because of Antd handling of error messages
                    throw new Error(t("Error: ${label} name can't contain emojis"));
                  }
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <EventTime
            disabledDuration={Boolean(event && hasProgramEventFinished(event)) && !isDuplicating}
            disabledTime={
              (Boolean(event && isProgramEventOngoing(event)) || Boolean(event && hasProgramEventFinished(event))) &&
              !isDuplicating
            }
            duration={{ label: t('Duration'), name: 'duration' }}
            end={{ name: 'endTime' }}
            form={form}
            maximum={program?.endTime}
            minimum={minimumStart}
            mode={mode}
            start={{
              label: t('Starts on'),
              name: 'startTime',
              tooltip: t("Times are shown in your browser's timezone ({{timeZone}}, {{offset}})", {
                offset,
                timeZone,
              }),
            }}
          />

          {mode === 'create' ? (
            <>
              <Title level={4} marginBottom>
                {isProgram ? 'Invitations' : 'Who do you want to invite?'}
              </Title>
              {isProgram ? (
                <Form.Item<DefaultEventFormValues> hidden name="viewPermission" />
              ) : (
                <CardTabsDeprecated
                  name="viewPermission"
                  onSelect={() => {
                    resetChannelsOptions();
                  }}
                  tabs={[
                    {
                      content: (
                        <CardTabDeprecated
                          subtitle="Only invited community members can join."
                          title="Select community members"
                        />
                      ),
                      id: 'select-community-members',
                      key: 'private',
                    },
                    {
                      content: (
                        <CardTabDeprecated
                          subtitle="You will invite all community members."
                          title="All community members"
                        />
                      ),
                      id: 'all-community-members',
                      key: 'public',
                    },
                  ]}
                />
              )}

              <Form.Item<DefaultEventFormValues> dependencies={['viewPermission']} noStyle>
                {({ getFieldValue }) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  const viewPermission: EventPrivacy = getFieldValue('viewPermission');

                  return (
                    <EventInviteesSelects<DefaultEventFormValues>
                      selects={[
                        {
                          buttonText: 'Invite',
                          key: 'moderator',
                          label: 'Admins / Hosts',
                          onPress: onShowInvitees(ProgramInviteType.moderators),
                          roleType: 'moderator',
                        },
                        {
                          buttonText: 'Invite',
                          key: 'presenter',
                          label: 'Presenters',
                          onPress: onShowInvitees(ProgramInviteType.presenters),
                          roleType: 'presenter',
                        },
                        {
                          buttonText: 'Invite',
                          disabled: viewPermission === 'public',
                          key: 'attendee',
                          label: 'Attendees',
                          onPress: onShowInvitees(ProgramInviteType.invitees),
                          roleType: 'attendee',
                        },
                      ]}
                    />
                  );
                }}
              </Form.Item>
            </>
          ) : (
            <Form.Item<DefaultEventFormValues> hidden name="viewPermission" />
          )}

          {mode === 'duplicate' ? <DuplicateInviteesSection eventId={event?.id} /> : null}

          {isProgram ? null : <EventChannelsOptions<DefaultEventFormValues> />}

          {mode === 'create' ? (
            // TODO: This is hack
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            <EventInviteesExceededLimit event={event} form={form as unknown as FormInstance<ExceedCommonFormValues>} />
          ) : null}

          <Title level={4} marginBottom>
            Additional details
          </Title>
          <RichTextInput
            html={{
              'data-test-id': 'event_description',
              label: 'Description',
              name: 'htmlContent',
            }}
            text={{ name: 'textContent' }}
          />
          <Form.Item<DefaultEventFormValues>
            getValueFromEvent={({ fileList }: UploadChangeParam): UploadedFileList => fileList}
            label="Event photo"
            name="coverPhoto"
            rules={imageRules}
            valuePropName="fileList"
          >
            <ImageDraggerWithCrop
              dragger={{
                loadImage,
                shape: 'rectangle-stretch',
                size: 'large',
                uploadFile: uploadImage,
                validations: imageValidations,
              }}
            />
          </Form.Item>

          {mode === 'edit' ? <AttachmentsInput item={{ label: 'Slides', name: 'eventsFiles' }} /> : null}

          <Modal.HugContent.Footer>
            <FormActions
              right={{
                children: cancelTitle,
                onClick: onCancel,
              }}
              submit={{
                children: submitTitle,
                id: 'btn_schedule-event',
                loading: isLoading,
              }}
            />
          </Modal.HugContent.Footer>
        </>
      )}
    </Form>
  );
};

export default DefaultEventForm;
